import React, { useState, useEffect } from 'react';
import { 
  ChartBarIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  TableCellsIcon,
  ShieldCheckIcon,
  ClockIcon,
  BellAlertIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid, XCircleIcon as XCircleSolid } from '@heroicons/react/24/solid';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { analyticsAPI } from '../../api/enhancedApiService';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const DataObservabilityDashboard = ({ 
  connectionId, 
  timeframe = 30, 
  analyticsData: propAnalyticsData = null,
  isLoading: propIsLoading = false 
}) => {
  // Use passed data if available, otherwise use local state
  const [localAnalyticsData, setLocalAnalyticsData] = useState(null);
  const [localLoading, setLocalLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Use prop data if available, otherwise fallback to local data
  const analyticsData = propAnalyticsData || localAnalyticsData;
  const loading = propIsLoading || localLoading;
  const selectedTimeframe = timeframe;

  useEffect(() => {
    // Only fetch data if we don't have prop data and we have a connection ID
    if (!propAnalyticsData && connectionId) {
      fetchAnalyticsData();
    } else if (propAnalyticsData) {
      // If we have prop data, we're not loading locally
      setLocalLoading(false);
    }
  }, [connectionId, selectedTimeframe, propAnalyticsData]);

  const fetchAnalyticsData = async () => {
    try {
      setLocalLoading(true);
      setError(null);
      
      const response = await analyticsAPI.getAnalyticsOverview(connectionId, { days: selectedTimeframe });
      setLocalAnalyticsData(response.data || response);
      
    } catch (err) {
      console.error('Error fetching analytics data:', err);
      setError('Failed to load analytics data');
    } finally {
      setLocalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div className="text-center py-8 text-gray-500">
        No analytics data available
      </div>
    );
  }

  const getHealthColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getHealthIcon = (score) => {
    if (score >= 80) return <CheckCircleIcon className="h-5 w-5" />;
    if (score >= 60) return <ExclamationTriangleIcon className="h-5 w-5" />;
    return <XCircleIcon className="h-5 w-5" />;
  };

  return (
    <div className="space-y-6">
      {/* Dashboard Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Data Observability Dashboard</h2>
        <div className="text-sm text-gray-500">
          Last {selectedTimeframe} days
        </div>
      </div>

      {/* Alerts Section */}
      {analyticsData.alerts && analyticsData.alerts.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center mb-3">
            <BellAlertIcon className="h-5 w-5 text-red-500 mr-2" />
            <h3 className="text-lg font-semibold">Active Alerts</h3>
          </div>
          <div className="space-y-2">
            {analyticsData.alerts.slice(0, 3).map((alert, index) => (
              <div
                key={index}
                className={`flex items-start p-3 rounded-lg ${
                  alert.severity === 'critical' ? 'bg-red-50 border border-red-200' :
                  alert.severity === 'warning' ? 'bg-yellow-50 border border-yellow-200' :
                  'bg-blue-50 border border-blue-200'
                }`}
              >
                <div className="flex-1">
                  <p className="font-medium text-sm">{alert.message}</p>
                  {alert.table && (
                    <p className="text-xs text-gray-600 mt-1">Table: {alert.table}</p>
                  )}
                </div>
                <span className="text-xs text-gray-500">
                  {new Date(alert.timestamp).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Overall Health Score */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-700">Overall Health</h3>
            <ShieldCheckIcon className="h-5 w-5 text-gray-400" />
          </div>
          <div className={`flex items-center ${getHealthColor(analyticsData.overall_health || 0).split(' ')[0]}`}>
            {getHealthIcon(analyticsData.overall_health || 0)}
            <span className="ml-2 text-3xl font-bold">
              {analyticsData.overall_health || 0}%
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Based on quality, coverage, and stability
          </p>
        </div>

        {/* Data Quality Score */}
        {analyticsData.data_quality && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-700">Data Quality</h3>
              <ChartBarIcon className="h-5 w-5 text-gray-400" />
            </div>
            <div className="flex items-baseline">
              <span className="text-3xl font-bold text-gray-900">
                {analyticsData.data_quality.current_score || 0}%
              </span>
            </div>
            <div className="mt-2 flex text-xs text-gray-600">
              <span className="flex items-center mr-3">
                <CheckCircleSolid className="h-4 w-4 text-green-500 mr-1" />
                {analyticsData.data_quality.passed || 0}
              </span>
              <span className="flex items-center">
                <XCircleSolid className="h-4 w-4 text-red-500 mr-1" />
                {analyticsData.data_quality.failed || 0}
              </span>
            </div>
          </div>
        )}

        {/* Active Validations */}
        {analyticsData.validation_health && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-700">Active Rules</h3>
              <CheckCircleIcon className="h-5 w-5 text-gray-400" />
            </div>
            <div className="flex items-baseline">
              <span className="text-3xl font-bold text-gray-900">
                {analyticsData.validation_health.active_rules || 0}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Covering {analyticsData.validation_health.coverage || 0} tables
            </p>
          </div>
        )}

        {/* Schema Changes */}
        {analyticsData.schema_stability && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-700">Schema Changes</h3>
              <TableCellsIcon className="h-5 w-5 text-gray-400" />
            </div>
            <div className="flex items-baseline">
              <span className="text-3xl font-bold text-gray-900">
                {analyticsData.schema_stability.total_changes || 0}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              In the last {selectedTimeframe} days
            </p>
          </div>
        )}
      </div>

      {/* Data Quality Trends */}
      {analyticsData.data_quality?.trends && analyticsData.data_quality.trends.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Data Quality Trends</h3>
          <div className="h-64">
            <Line
              data={{
                labels: analyticsData.data_quality.trends.map(t => t.date),
                datasets: [
                  {
                    label: 'Success Rate',
                    data: analyticsData.data_quality.trends.map(t => t.success_rate),
                    borderColor: 'rgb(34, 197, 94)',
                    backgroundColor: 'rgba(34, 197, 94, 0.1)',
                    tension: 0.3,
                  },
                  {
                    label: 'Failed Checks',
                    data: analyticsData.data_quality.trends.map(t => t.failed),
                    borderColor: 'rgb(239, 68, 68)',
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                    tension: 0.3,
                    yAxisID: 'y1',
                  }
                ]
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'top',
                  },
                },
                scales: {
                  y: {
                    type: 'linear',
                    display: true,
                    position: 'left',
                    title: {
                      display: true,
                      text: 'Success Rate (%)'
                    }
                  },
                  y1: {
                    type: 'linear',
                    display: true,
                    position: 'right',
                    title: {
                      display: true,
                      text: 'Failed Checks'
                    },
                    grid: {
                      drawOnChartArea: false,
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      )}

      {/* Tables and Coverage */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Largest Tables */}
        {analyticsData.table_metrics?.largest_tables && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Largest Tables</h3>
            <div className="space-y-3">
              {analyticsData.table_metrics.largest_tables.map((table, index) => (
                <div key={index} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div>
                    <p className="font-medium text-sm">{table.name}</p>
                    <p className="text-xs text-gray-500">
                      {table.column_count} columns
                    </p>
                  </div>
                  <span className="text-sm font-semibold text-gray-700">
                    {(table.row_count || 0).toLocaleString()} rows
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total Tables:</span>
                <span className="font-semibold">{analyticsData.table_metrics.total_tables}</span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span className="text-gray-600">Total Rows:</span>
                <span className="font-semibold">
                  {(analyticsData.table_metrics.total_rows || 0).toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Validation Coverage */}
        {analyticsData.validation_health?.rules_by_type && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Validation Coverage</h3>
            <div className="h-48">
              <Doughnut
                data={{
                  labels: Object.keys(analyticsData.validation_health.rules_by_type),
                  datasets: [{
                    data: Object.values(analyticsData.validation_health.rules_by_type),
                    backgroundColor: [
                      'rgba(99, 102, 241, 0.8)',
                      'rgba(34, 197, 94, 0.8)',
                      'rgba(251, 191, 36, 0.8)',
                      'rgba(239, 68, 68, 0.8)',
                      'rgba(168, 85, 247, 0.8)',
                    ],
                    borderWidth: 0,
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'right',
                      labels: {
                        padding: 10,
                        font: {
                          size: 11
                        }
                      }
                    },
                  },
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Recent Activity */}
      {analyticsData.recent_activity?.recent_jobs && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
          <div className="space-y-2">
            {analyticsData.recent_activity.recent_jobs.map((job, index) => (
              <div key={index} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                <div className="flex items-center">
                  <ClockIcon className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-sm font-medium">{job.type}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    job.status === 'completed' ? 'bg-green-100 text-green-800' :
                    job.status === 'failed' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {job.status}
                  </span>
                  <span className="text-xs text-gray-500">
                    {new Date(job.created_at).toLocaleString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DataObservabilityDashboard;